"use client";
import {
  APIService,
  DynamicForm,
  useApiService,
  withFormService,
} from "@art/shared-ui";
import { validators } from "@art/shared-ui";
import { useIDPContext } from '../../../auth';
import { useEffect, useState } from "react";
import { ART_URL } from "../../../Constant";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Autocomplete, Box, TextField } from "@mui/material";

type ScopeOption = { label: string; value: string };

function ScopeMultiSelect({ label, value, onChange, loadOptions, error }: any) {
  const [options, setOptions] = useState<ScopeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const selected = selectedValues
    .map((item: any) => typeof item === "object" ? item.value : item)
    .map((item: string) => options.find((option) => option.value === item) || { label: item === "*" ? `All ${label.toLowerCase()}` : item, value: item });

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadOptions().then((items: ScopeOption[]) => active && setOptions(items)).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [label]);

  return <Box sx={{ mb: 2.25 }}>
    <Autocomplete
      multiple
      size="small"
      fullWidth
      disableCloseOnSelect
      loading={loading}
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      sx={(theme) => ({
        "& .MuiAutocomplete-tag": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
          "& .MuiChip-deleteIcon": {
            color: theme.palette.primary.contrastText,
            opacity: 0.72,
            "&:hover": { opacity: 1 },
          },
        },
        "& .MuiFormHelperText-root": {
          position: "static",
          transform: "none",
          margin: theme.spacing(0.5, 0, 0),
          color: theme.palette.text.secondary,
        },
      })}
      onChange={(_, items) => {
        const normalized = items.some((item) => item.value === "*")
          ? [items.find((item) => item.value === "*")!]
          : items;
        onChange?.({ target: { value: normalized } });
      }}
      renderInput={(params) => <TextField {...params} label={label} error={!!error} helperText={error || "Select one or more"} />}
    />
  </Box>;
}

function ArtUserAddPage({ data, onSave, onInit }: any) {
  const apiService = useApiService();
  const { authGuardInstance } = useIDPContext() as {
    authGuardInstance: any;
  };
  const [roles, setRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState<boolean>(!data);
  const authState = useSelector((state: RootState) => state.auth);

  const idpService = new APIService({
    ART_STORE: apiService.getStore(),
    BASE_URL: ART_URL + "/idp/api",
    DisableDefaultHeadder: true,
    DisableDefaultTenantUrl: true,
  });

  useEffect(() => {
    if (data) return;
    const tenantName = authState.tenantName;
    if (!tenantName) {
      setRolesLoading(false);
      return;
    }
    idpService
      .get(`/tenants/${tenantName}/roles`)
      .then(({ data: { data: payload } }: any) => {
        setRoles(payload?.role ?? []);
      })
      .catch(() => setRoles([]))
      .finally(() => setRolesLoading(false));
  }, [data]);

  const formBuilder = (formData: any) => {
    const baseFields = [
      {
        type: "hidden",
        id: "tenant_name",
        value: authState.tenantName,
      },
      {
        type: "hidden",
        id: "sender",
        value: authGuardInstance?.user?.email || authState.user?.email || "",
      },
    ];

    if (data) {
      return [
        {
          type: "row",
          children: [
            {
              type: "col",
              classNames: ["col-12"],
              children: [
                {
                  id: "first_name",
                  label: "First Name",
                  type: "text",
                  placeholder: "Enter first name",
                  required: true,
                  value: formData?.first_name || data?.first_name,
                  dataQa: "app-user-firstname",
                  validate: validators((value) => {
                    if (!value || value.trim() === "") {
                      return "First name is required";
                    }
                    return null;
                  }),
                },
              ],
            },
            {
              type: "col",
              classNames: ["col-12"],
              children: [
                {
                  id: "last_name",
                  label: "Last Name",
                  type: "text",
                  placeholder: "Enter last name",
                  required: false,
                  value: formData?.last_name || data?.last_name,
                  dataQa: "app-user-lastname",
                },
              ],
            },
          ],
        },
        {
          type: "row",
          children: [
            {
              type: "col",
              classNames: ["col-12"],
              children: [
                {
                  id: "email",
                  type: "hidden",
                  value: formData?.email || data?.email,
                  dataQa: "app-user-email",
                },
              ],
            },
          ],
        },
      ];
    }

    return [
      ...baseFields,
      {
        type: "row",
        children: [
          {
            type: "col",
            classNames: ["col-12"],
            children: [
              {
                id: "receiver",
                label: "Email",
                type: "email",
                placeholder: "Enter email",
                required: true,
                value: formData?.receiver,
                marginBottom: 16,
                dataQa: "app-user-email",
                validate: validators((value) => {
                  if (!value || value.trim() === "") {
                    return "Email is required.";
                  }
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return "Email must be a valid email address.";
                  }
                  return null;
                }),
              },
            ],
          },
        ],
      },
      {
        type: "row",
        children: [
          {
            type: "col",
            classNames: ["col-12"],
            children: [
              {
                id: "role_id",
                label: "Role",
                type: "select",
                placeholder: "Select a role",
                required: true,
                value: formData?.role_id,
                marginBottom: 16,
                dataQa: "app-user-role",
                getOptions: async () => roles,
                getOptionLabel: (r: any) => r.name,
                getOptionValue: (r: any) => r.id,
                validate: validators((value) => {
                  if (value === undefined || value === null || value === "") {
                    return "Role is required.";
                  }
                  return null;
                }),
              },
            ],
          },
        ],
      },
      {
        type: "row",
        children: [
          {
            type: "col",
            classNames: ["col-12"],
            children: [
              {
                id: "scope_projects",
                label: "Projects",
                type: "custom",
                required: true,
                value: formData?.scope_projects || ["*"],
                builder: (props: any) => <ScopeMultiSelect {...props} label="Projects" loadOptions={async () => {
                  const response = await apiService.get("/resource-access-catalog/projects");
                  return [
                    { label: "All projects", value: "*" },
                    ...(response.data?.data?.projects || []).map((item: any) => ({
                      label: item.title,
                      value: item.project_key,
                    })),
                  ];
                }} />,
                validate: validators((value) => !value?.length ? "Select at least one project or All projects." : null),
              },
            ],
          },
        ],
      },
      {
            type: "row",
            children: [{
              type: "col",
              classNames: ["col-12"],
              children: [{
                id: "scope_environments",
                label: "Environments",
                type: "custom",
                required: true,
                value: formData?.scope_environments || ["*"],
                builder: (props: any) => <ScopeMultiSelect {...props} label="Environments" loadOptions={async () => {
                  const response = await apiService.get("/resource-access-catalog/environments?limit=100");
                  return [
                    { label: "All environments", value: "*" },
                    ...(response.data?.data?.environments || []).map((item: any) => ({ label: item.title, value: item.title })),
                  ];
                }} />,
                validate: validators((value) => !value?.length ? "Select at least one environment or All environments." : null),
              }],
            }],
      },
    ];
  };

  const normalizeFormData = (formData: Record<string, any>) => {
    const normalized: Record<string, any> = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        if (typeof value === "string") {
          return [key, value.trim()];
        }
        return [key, value];
      }),
    );
    if (
      normalized.role_id !== undefined &&
      normalized.role_id !== null &&
      normalized.role_id !== ""
    ) {
      normalized.role_id = Number(normalized.role_id);
    }
    if (!data) {
      const asOptions = (value: any, fallbackLabel: string): ScopeOption[] => {
        const values = Array.isArray(value) ? value : value === undefined || value === null || value === "" ? ["*"] : [value];
        return values.map((item: any) => typeof item === "object" && item !== null
          ? { value: item.value, label: item.label || item.value }
          : { value: item, label: item === "*" ? fallbackLabel : String(item) }).filter((item) => item.value);
      };
      const selectedProjects = asOptions(normalized.scope_projects, "All projects");
      const selectedEnvironments = asOptions(normalized.scope_environments, "All environments");
      const projects = selectedProjects.some((item) => item.value === "*") ? [{ value: "*", label: "All projects" }] : selectedProjects;
      const environments = selectedEnvironments.some((item) => item.value === "*") ? [{ value: "*", label: "All environments" }] : selectedEnvironments;
      normalized.resource_scopes = projects.flatMap((project) =>
        environments.map((environment) => ({
          project_key: project.value,
          project_title: project.label,
          environment_name: environment.value,
          environment_title: environment.label,
        })),
      );
      delete normalized.scope_projects;
      delete normalized.scope_environments;
    }
    return normalized;
  };

  if (rolesLoading) return null;

  return (
    <DynamicForm
      formBuilder={formBuilder}
      onInit={onInit}
      onSubmit={(formData: Record<string, any>) => {
        const normalizedData = normalizeFormData(formData);

        if (!data) {
          return idpService.saveData("/invite", "post", {
            onSuccess: () => {
              onSave();
            },

            onError: (err: any) => {
              console.error(err);
            },
          })(normalizedData);
        } else {
          const userUpdateData = {
            first_name: normalizedData.first_name,
            last_name: normalizedData.last_name,
            email: normalizedData.email,
          };

          return apiService.saveData(`/user/${data.id}`, "put", {
            onSuccess: () => {
              onSave();
            },
            onError: (err: any) => {
              console.log(err);
            },
          })(userUpdateData);
        }
      }}
    />
  );
}

export default withFormService(ArtUserAddPage);
